// apps/web/src/components/checklist/ChecklistView.tsx
import { useState, useEffect, useCallback } from 'react';
import { getDateKey } from '@homework-app/shared';
import type { DailyChecklist, ChecklistStatus } from '@homework-app/shared';
import { ChecklistItem } from './ChecklistItem';
import { RewardBanner } from './RewardBanner';

interface Props {
  apiService: {
    getChecklist(dateKey: string): Promise<DailyChecklist>;
    updateChecklistStatus(itemId: string, status: ChecklistStatus, comment?: string): Promise<void>;
    uploadChecklistPhoto(itemId: string, photo: File, phase: string): Promise<{ id: string; url: string }>;
    completeTimer(itemId: string): Promise<void>;
    completeHonor(itemId: string): Promise<void>;
  };
}

export function ChecklistView({ apiService }: Props) {
  const [checklist, setChecklist] = useState<DailyChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateKey] = useState(() => getDateKey(new Date()));

  const loadChecklist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getChecklist(dateKey);
      setChecklist(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  }, [apiService, dateKey]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  if (loading) {
    return <div className="checklist-loading">Loading today's checklist...</div>;
  }

  if (error) {
    return (
      <div className="checklist-error">
        <p>{error}</p>
        <button onClick={loadChecklist}>Try Again</button>
      </div>
    );
  }

  if (!checklist || checklist.items.length === 0) {
    return (
      <div className="checklist-empty">
        <h2>No checklist for today</h2>
        <p>Ask your parent to set up your daily checklist.</p>
      </div>
    );
  }

  return (
    <div className="checklist-view">
      <header className="checklist-header">
        <h1>Today's Checklist</h1>
        <span className="checklist-progress">
          {checklist.completed_count} of {checklist.total_count} done
        </span>
      </header>

      <RewardBanner
        reward={checklist.reward}
        allApproved={checklist.all_approved}
        completedCount={checklist.completed_count}
        totalCount={checklist.total_count}
      />

      <div className="checklist-items">
        {checklist.items.map(item => (
          <ChecklistItem
            key={item.id}
            item={item}
            onStatusChange={loadChecklist}
            apiService={apiService}
          />
        ))}
      </div>
    </div>
  );
}
